import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ChevronDown, Luggage, Sparkles, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api, { trackActivity } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/ui-kit";

const SECTIONS = [
  {
    title: "Essential Documents",
    tip: "Keep your essential documents in your cabin bag and create a secure digital backup before you fly.",
    items: ["Passport", "Student Visa / Entry Clearance", "CAS Letter", "University Offer Letter", "Accommodation Confirmation", "Flight Tickets", "Travel Insurance Documents", "TB Certificate where applicable", "Class 10 Certificate", "Class 12 Certificate", "Graduation Certificate", "Postgraduate Certificate where applicable", "Degree Certificates", "IELTS / PTE / TOEFL Scorecard", "Bank Statements where required", "Loan Sanction Letter where required", "Sponsorship Documents where required", "Passport Photos", "Emergency Contacts", "Driving Licence if available", "International Debit/Credit Card", "Forex Card", "Physical copies of important documents", "Digital copies of important documents"],
  },
  {
    title: "Clothes",
    tip: "These are suggested quantities, not rules. You can buy clothing in the UK, so don't fill your suitcase unnecessarily.",
    items: ["T-Shirts · 8-10", "Shirts · 4-6", "Jeans · 3-4", "Trousers · 2-3", "Joggers · 2", "Shorts · 2", "Hoodies · 2-3", "Sweatshirts · 2", "Sweaters · 2", "Light Waterproof Jacket · 1", "Winter Jacket · 1", "Thermal Innerwear · 2 sets", "Socks · 10-12 pairs", "Underwear · 10-14 sets", "Nightwear · 2 sets", "Formal Outfit · 1", "Traditional Indian Outfit · 1-2"],
  },
  {
    title: "Footwear & Weather",
    items: ["Casual Shoes", "Sports Shoes", "Formal Shoes", "Slippers", "Flip-Flops", "Beanie / Cap", "Scarf", "Gloves", "Umbrella", "Raincoat if required"],
  },
  {
    title: "Medicines & First Aid",
    tip: "Keep medicines in their original packaging where possible and carry supporting documentation for prescription medication.",
    items: ["Prescription Medicines", "Paracetamol", "Cold & Flu Medication", "Cough Medication", "Antihistamines where appropriate", "ORS", "Basic Stomach Remedies", "Pain Relief where appropriate", "Band-Aids", "Crepe Bandage", "Cotton", "Antiseptic Cream"],
  },
  {
    title: "Electronics",
    tip: "The UK uses Type G plugs.",
    items: ["Laptop", "Laptop Charger", "Phone", "Phone Charger", "Power Bank", "Earphones", "Headphones", "USB Pen Drive", "External Hard Drive if required", "Calculator", "Smartwatch if required", "Extension Board", "USB Charging Hub", "Portable Mouse", "Laptop Sleeve", "UK Plug Adapter"],
  },
  {
    title: "Kitchen Essentials",
    tip: "Don't purchase and carry anything else from home, it's a similar price to directly buy as you arrive. Instead pack some good snacks from home, the UK won't give you tasty snacks like these.",
    items: ["Pressure Cooker"],
  },
  {
    title: "Food & Snacks from India",
    tip: "Food import rules can change. Always check the latest UK government guidance before travelling.",
    items: ["Maggi", "Poha", "Upma Mix", "Ready-to-Eat Meals", "Tea Bags", "Coffee Sachets", "Instant Soup", "Biscuits", "Protein Bars", "Dry Fruits", "Roasted Chana", "Peanuts", "Khakra", "Thepla", "Pickle", "Protein Powder if required"],
  },
  {
    title: "Toiletries",
    items: ["Toothbrush", "Toothpaste", "Soap", "Face Wash", "Moisturiser", "Shampoo", "Hair Oil", "Comb", "Razor", "Shaving Kit", "Nail Cutter", "Deodorant", "Perfume", "Towels", "Hand Towel", "Personal Hygiene Products"],
  },
  {
    title: "Bedding",
    tip: "Check your accommodation first. Many student accommodations already provide bedding.",
    items: ["Bedsheet", "Pillow Cover", "Light Blanket", "Mattress Protector"],
  },
  {
    title: "Stationery",
    items: ["Notebooks", "Pens", "Highlighters", "Sticky Notes", "Folder", "Document Organiser", "Stapler", { label: "Scissors", note: "Checked baggage only." }],
  },
  {
    title: "Financial Essentials",
    tip: "Carry an amount of GBP cash you're comfortable with. There's no fixed rule.",
    items: ["International Debit Card", "Credit Card", "Forex Card if required", "Initial GBP cash", "Bank Details", "Emergency Cash"],
  },
  {
    title: "Personal Care",
    items: ["Spectacles", "Spare Glasses", "Contact Lenses", "Lens Solution", "Hair Trimmer", "Hair Dryer if required", "Makeup / Personal Care Products if required"],
  },
  {
    title: "Apps to Install Before Flying",
    items: ["University App", "WhatsApp", "Google Maps", "Uber", "Citymapper where relevant", "Trainline", "National Rail", "NHS App", "Microsoft Authenticator", "Banking App", "Student Beans", "UNiDAYS"],
  },
];

const CABIN_BAG = ["Passport", "Visa / Entry Documentation", "CAS", "University Documents", "Accommodation Details", "Phone", "Phone Charger", "Laptop", "Medication", "Wallet / Cards", "Important Documents", "Emergency Contacts", "One Change of Clothes"];

const DONT_NEED = ["Heavy Blankets", "Large quantities of utensils", "Buckets", "Mugs", "Iron", "Electric Kettle", "Rice Cooker unless specifically required", "Mixer Grinder", "Large Shampoo Bottles", "Heavy Textbooks", "Excessive Formal Clothing", "Too Many Winter Jackets", "Fresh Food", "Homemade Curries", "Fresh Fruits / Vegetables where restricted", "Gas Appliances", "Large Detergent Bottles", "Decorative Items", "Too Many Shoes"];

const SMART_TIPS = [
  "Use vacuum storage bags.",
  "Roll clothes instead of folding them.",
  "Keep one change of clothes in your cabin bag.",
  "Label your suitcase.",
  "Weigh your luggage before leaving for the airport.",
  "Keep important documents, valuables, medicines and essential electronics in your cabin baggage where permitted.",
  "Keep important documents in a waterproof folder.",
  "Download important documents and offline maps before departure.",
];

const UNLOCKED_FEATURES = ["Castles AI", "Live Support", "Part-Time Job Guidance", "Money-Saving Tips", "Student Connections", "Members-Only Events", "UK Arrival Guidance"];

const itemId = (s, i) => `s${s}-i${i}`;
const itemLabel = (item) => (typeof item === "string" ? item : item.label);
const itemNote = (item) => (typeof item === "string" ? null : item.note);

const CheckRow = ({ id, label, note, checked, onToggle, accent }) => (
  <button
    data-testid={`pack-item-${id}`}
    onClick={() => onToggle(id, !checked)}
    className={`w-full flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
      checked
        ? accent ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.08]" : "border-[#D4AF37]/50 bg-[#D4AF37]/[0.08]"
        : "border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40"
    }`}
  >
    <span className={`w-6 h-6 rounded-lg border flex items-center justify-center shrink-0 transition-colors duration-300 ${checked ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25"}`}>
      {checked && <Check className="w-4 h-4 text-[#0A1633]" strokeWidth={3} />}
    </span>
    <span>
      <span className={`block ${checked ? "text-white line-through opacity-70" : "text-slate-200"}`}>{label}</span>
      {note && <span className="block text-xs text-[#D4AF37] mt-1">{note}</span>}
    </span>
  </button>
);

const Section = ({ title, tip, items, sectionIdx, packed, onToggle, open, onToggleOpen, accent }) => {
  const done = items.filter((_, i) => packed.has(itemId(sectionIdx, i))).length;
  return (
    <div className={`rounded-3xl border overflow-hidden ${accent ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.04]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`pack-section-${sectionIdx}`}>
      <button
        data-testid={`pack-section-toggle-${sectionIdx}`}
        onClick={onToggleOpen}
        className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5 transition-colors duration-300 hover:bg-white/[0.03]"
      >
        <span className="flex items-center gap-4">
          <span className="text-left text-lg text-white font-display">{title}</span>
          <span className="text-xs text-slate-500 shrink-0">{done}/{items.length}</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 sm:px-8 pb-6 space-y-3">
          {items.map((item, i) => (
            <CheckRow key={i} id={itemId(sectionIdx, i)} label={itemLabel(item)} note={itemNote(item)}
              checked={packed.has(itemId(sectionIdx, i))} onToggle={onToggle} accent={accent} />
          ))}
          {tip && (
            <p className="text-sm text-[#D4AF37]/90 bg-[#D4AF37]/[0.07] border border-[#D4AF37]/20 rounded-2xl px-5 py-4 leading-relaxed">
              Castles Tip: {tip}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default function PackingChecklist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packed, setPacked] = useState(new Set());
  const [total, setTotal] = useState(180);
  const [openSections, setOpenSections] = useState({ 0: true });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    trackActivity("page_visit", "Opened packing checklist", "packing");
    api.get("/packing").then((r) => {
      setPacked(new Set(r.data.packed_ids));
      setTotal(r.data.total);
      setLoaded(true);
    }).catch(() => toast.error("Could not load your checklist. Please refresh."));
  }, []);

  const toggle = async (id, next) => {
    setPacked((prev) => {
      const copy = new Set(prev);
      next ? copy.add(id) : copy.delete(id);
      return copy;
    });
    try {
      await api.post("/packing/toggle", { item_id: id, packed: next });
    } catch {
      setPacked((prev) => {
        const copy = new Set(prev);
        next ? copy.delete(id) : copy.add(id);
        return copy;
      });
      toast.error("Could not save. Try again.");
    }
  };

  const toggleSection = (idx) => setOpenSections((s) => ({ ...s, [idx]: !s[idx] }));

  const count = packed.size;
  const pct = total ? Math.round((count / total) * 100) : 0;
  const complete = loaded && count >= total;

  return (
    <div className="min-h-screen bg-[#060D1F]" data-testid="packing-page">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <button data-testid="back-to-portal" onClick={() => navigate("/portal")}
            className="flex items-center gap-2 text-sm text-slate-400 transition-colors duration-300 hover:text-[#D4AF37]">
            <ArrowLeft className="w-4 h-4" /> Back to Portal
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3">
            <Luggage className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.7} />
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">Pre-Flight</p>
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight" data-testid="packing-heading">Packing Checklist</h1>
          <p className="mt-3 text-slate-400">Tick items off as you pack. Everything saves automatically to your account.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8 rounded-3xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-7 sm:p-8" data-testid="packing-progress">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-400">Your Packing Progress</p>
              <p className="mt-2 text-2xl sm:text-3xl font-display text-white" data-testid="packing-count">{count} / {total} items packed</p>
            </div>
            <p className="text-3xl font-display text-[#D4AF37]" data-testid="packing-percent">{pct}%</p>
          </div>
          <div className="mt-5 h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </motion.div>

        {complete && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 rounded-3xl border border-[#D4AF37]/40 bg-[#0A1633] p-8 sm:p-10 text-center" data-testid="packing-complete">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
              <PartyPopper className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.6} />
            </div>
            <h2 className="mt-6 text-2xl sm:text-3xl text-white tracking-tight">You're UK-ready, {user?.first_name || "friend"}!</h2>
            <p className="mt-3 text-slate-300">Your suitcase is sorted. Now let's get you ready for life in the UK.</p>
            <p className="mt-6 text-sm tracking-[0.2em] uppercase text-[#D4AF37]">Packing is only the beginning</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2.5">
              {UNLOCKED_FEATURES.map((f) => (
                <span key={f} className="text-xs rounded-full border border-white/15 text-slate-300 px-4 py-1.5">{f}</span>
              ))}
            </div>
            <button data-testid="explore-castles-button" onClick={() => navigate("/portal")}
              className="mt-8 rounded-full bg-[#D4AF37] text-[#0A1633] font-semibold px-8 py-3.5 transition-all duration-300 hover:bg-[#e5c558] hover:-translate-y-0.5">
              Explore Castles
            </button>
          </motion.div>
        )}

        <div className="mt-10 space-y-4">
          {SECTIONS.map((s, i) => (
            <Section key={s.title} title={s.title} tip={s.tip} items={s.items} sectionIdx={i}
              packed={packed} onToggle={toggle} open={!!openSections[i]} onToggleOpen={() => toggleSection(i)} />
          ))}

          <Section title="Your Cabin Bag: Don't Forget These" items={CABIN_BAG} sectionIdx={SECTIONS.length}
            packed={packed} onToggle={toggle} open={!!openSections[SECTIONS.length]} onToggleOpen={() => toggleSection(SECTIONS.length)} accent />

          <div className="rounded-3xl border border-red-400/20 bg-red-500/[0.04] overflow-hidden" data-testid="pack-section-dont-need">
            <button data-testid="pack-section-toggle-dont-need" onClick={() => toggleSection("x")}
              className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5 transition-colors duration-300 hover:bg-white/[0.03]">
              <span className="flex items-center gap-4">
                <span className="text-left text-lg text-white font-display">Don't Waste Your Luggage Space</span>
                <span className="text-xs text-slate-500 shrink-0">{DONT_NEED.filter((_, i) => packed.has(itemId("x", i))).length}/{DONT_NEED.length}</span>
              </span>
              <ChevronDown className={`w-5 h-5 text-red-300 shrink-0 transition-transform duration-300 ${openSections["x"] ? "rotate-180" : ""}`} />
            </button>
            {openSections["x"] && (
              <div className="px-6 sm:px-8 pb-6 space-y-3">
                <p className="text-sm text-slate-400 leading-relaxed">Tick these off once you've decided to leave them behind. You'll thank yourself at the airport.</p>
                {DONT_NEED.map((item, i) => (
                  <CheckRow key={i} id={itemId("x", i)} label={item} checked={packed.has(itemId("x", i))} onToggle={toggle} />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-8" data-testid="smart-packing-tips">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.8} />
              <h2 className="text-lg text-white font-display">Smart Packing Tips</h2>
            </div>
            <ul className="mt-5 space-y-3">
              {SMART_TIPS.map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
